import type {
	AssignPolicyRequest,
	OutOfWindowPolicy,
	WorkPolicyBreak,
	WorkPolicyRequest,
	WorkPolicyResponse,
	WorkPolicyShift,
	WorkPolicyType
} from '$lib/api/types';

export type WorkPolicyFormValues = {
	name: string;
	type: WorkPolicyType;
	workStartTime: string;
	workEndTime: string;
	lateGraceMinutes: number;
	checkInWindowStart: string;
	checkInWindowEnd: string;
	outOfWindowPolicy: OutOfWindowPolicy;
	dailyWorkMinutes: number;
	totalBreakMinutes: number;
	autoCheckOutAfterHours: number;
	attendanceAdjustmentRequestDeadlineDays: number;
	unapprovedOvertimeRecognized: boolean;
	overtimeMinimumRequestMinutes: number;
	overtimeCompensationMultiplier: number;
	nightCompensationMultiplier: number;
	holidayCompensationMultiplier: number;
	shifts: WorkPolicyShift[];
	breaks: WorkPolicyBreak[];
};

export type AssignPolicyFormValues = {
	employeeIds: number[];
	effectiveFrom: string;
};

type ValidationResult<T> = { ok: true; request: T } | { ok: false; message: string };

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function validateWorkPolicyForm(
	values: WorkPolicyFormValues
): ValidationResult<WorkPolicyRequest> {
	const name = values.name.trim();
	if (!name) {
		return { ok: false, message: '이름은 필수입니다.' };
	}
	if (name.length > 100) {
		return { ok: false, message: '이름은 100자 이하로 입력해주세요.' };
	}
	if (!isIntegerInRange(values.dailyWorkMinutes, 1, 1440)) {
		return { ok: false, message: '일일 근무 시간은 1분 이상 24시간 이하로 입력해주세요.' };
	}
	if (
		!Number.isInteger(values.totalBreakMinutes) ||
		values.totalBreakMinutes < 0 ||
		values.totalBreakMinutes > values.dailyWorkMinutes
	) {
		return { ok: false, message: '휴게 시간은 0분 이상 일일 근무 시간 이하로 입력해주세요.' };
	}
	if (!isIntegerInRange(values.autoCheckOutAfterHours, 1, 48)) {
		return { ok: false, message: '자동 퇴근 시간은 1~48시간 사이로 입력해주세요.' };
	}
	if (!isIntegerInRange(values.attendanceAdjustmentRequestDeadlineDays, 0, 365)) {
		return { ok: false, message: '근태 정정 신청기한은 0~365일 사이로 입력해주세요.' };
	}
	if (!isIntegerInRange(values.overtimeMinimumRequestMinutes, 1, 1440)) {
		return { ok: false, message: '연장근무 최소 신청 시간은 1~1440분 사이로 입력해주세요.' };
	}
	if (
		!isPositiveNumberAtMost(values.overtimeCompensationMultiplier, 10) ||
		!isPositiveNumberAtMost(values.nightCompensationMultiplier, 10) ||
		!isPositiveNumberAtMost(values.holidayCompensationMultiplier, 10)
	) {
		return { ok: false, message: '보상 배율은 0 초과 10 이하로 입력해주세요.' };
	}

	const breaks = validateBreaks(values.breaks);
	if (!breaks.ok) return breaks;

	const baseRequest = {
		name,
		type: values.type,
		dailyWorkMinutes: values.dailyWorkMinutes,
		totalBreakMinutes: values.totalBreakMinutes,
		autoCheckOutAfterHours: values.autoCheckOutAfterHours,
		attendanceAdjustmentRequestDeadlineDays: values.attendanceAdjustmentRequestDeadlineDays,
		unapprovedOvertimeRecognized: values.unapprovedOvertimeRecognized,
		overtimeMinimumRequestMinutes: values.overtimeMinimumRequestMinutes,
		overtimeCompensationMultiplier: values.overtimeCompensationMultiplier,
		nightCompensationMultiplier: values.nightCompensationMultiplier,
		holidayCompensationMultiplier: values.holidayCompensationMultiplier,
		breaks: breaks.request
	};

	if (values.type === 'FIXED') {
		const timeError = validateTimePair(
			values.workStartTime,
			values.workEndTime,
			'출근과 퇴근 시간을 모두 입력해주세요.',
			'출근/퇴근 시간 형식이 올바르지 않습니다.',
			'퇴근 시간은 출근 시간보다 늦어야 합니다.'
		);
		if (timeError) return timeError;
		if (!isIntegerInRange(values.lateGraceMinutes, 0, 1440)) {
			return { ok: false, message: '지각 허용 시간은 0분 이상으로 입력해주세요.' };
		}

		return {
			ok: true,
			request: {
				...baseRequest,
				workStartTime: values.workStartTime,
				workEndTime: values.workEndTime,
				lateGraceMinutes: values.lateGraceMinutes
			}
		};
	}

	if (values.type === 'FLEXIBLE') {
		const timeError = validateTimePair(
			values.checkInWindowStart,
			values.checkInWindowEnd,
			'출근 허용 시작과 종료 시간을 모두 입력해주세요.',
			'출근 허용 시간 형식이 올바르지 않습니다.',
			'출근 허용 종료 시간은 시작 시간보다 늦어야 합니다.'
		);
		if (timeError) return timeError;

		return {
			ok: true,
			request: {
				...baseRequest,
				checkInWindowStart: values.checkInWindowStart,
				checkInWindowEnd: values.checkInWindowEnd,
				outOfWindowPolicy: values.outOfWindowPolicy
			}
		};
	}

	const shifts = validateShifts(values.shifts);
	if (!shifts.ok) return shifts;

	return {
		ok: true,
		request: {
			...baseRequest,
			shifts: shifts.request
		}
	};
}

export function validateAssignPolicyForm(
	values: AssignPolicyFormValues
): ValidationResult<AssignPolicyRequest> {
	if (values.employeeIds.length === 0) {
		return { ok: false, message: '직원을 최소 1명 이상 선택해주세요.' };
	}
	if (!values.effectiveFrom) {
		return { ok: false, message: '적용 시작일을 입력해주세요.' };
	}
	if (!isValidDateString(values.effectiveFrom)) {
		return { ok: false, message: '적용 시작일 형식이 올바르지 않습니다.' };
	}

	return {
		ok: true,
		request: {
			employeeIds: [...new Set(values.employeeIds)],
			effectiveFrom: values.effectiveFrom
		}
	};
}

export function formFromPolicy(policy: WorkPolicyResponse): WorkPolicyFormValues {
	return {
		name: policy.name,
		type: policy.type,
		workStartTime: policy.workStartTime ?? '09:00',
		workEndTime: policy.workEndTime ?? '18:00',
		lateGraceMinutes: policy.lateGraceMinutes ?? 0,
		checkInWindowStart: policy.checkInWindowStart ?? '08:00',
		checkInWindowEnd: policy.checkInWindowEnd ?? '10:00',
		outOfWindowPolicy: policy.outOfWindowPolicy ?? 'FORCE_LATE',
		dailyWorkMinutes: policy.dailyWorkMinutes,
		totalBreakMinutes: policy.totalBreakMinutes,
		autoCheckOutAfterHours: policy.autoCheckOutAfterHours,
		attendanceAdjustmentRequestDeadlineDays: policy.attendanceAdjustmentRequestDeadlineDays,
		unapprovedOvertimeRecognized: policy.unapprovedOvertimeRecognized,
		overtimeMinimumRequestMinutes: policy.overtimeMinimumRequestMinutes,
		overtimeCompensationMultiplier: policy.overtimeCompensationMultiplier,
		nightCompensationMultiplier: policy.nightCompensationMultiplier,
		holidayCompensationMultiplier: policy.holidayCompensationMultiplier,
		shifts: policy.shifts.map((shift, index) => ({
			id: shift.id,
			label: shift.label,
			startTime: shift.startTime,
			endTime: shift.endTime,
			lateGraceMinutes: shift.lateGraceMinutes,
			displayOrder: index
		})),
		breaks: policy.breaks.map((brk, index) => ({
			id: brk.id,
			startTime: brk.startTime,
			endTime: brk.endTime,
			paid: brk.paid,
			displayOrder: index
		}))
	};
}

function validateShifts(
	shifts: WorkPolicyShift[]
): ValidationResult<WorkPolicyShift[]> {
	if (shifts.length === 0) {
		return { ok: false, message: '시차 근무제는 시프트를 최소 1개 이상 추가해주세요.' };
	}

	const request: WorkPolicyShift[] = [];
	for (const [index, shift] of shifts.entries()) {
		const label = shift.label.trim();
		if (!label) {
			return { ok: false, message: `${index + 1}번째 시프트 라벨을 입력해주세요.` };
		}
		const timeError = validateTimePair(
			shift.startTime,
			shift.endTime,
			`${index + 1}번째 시프트 시간을 모두 입력해주세요.`,
			`${index + 1}번째 시프트 시간 형식이 올바르지 않습니다.`,
			'시프트 종료 시간은 시작 시간보다 늦어야 합니다.'
		);
		if (timeError) return timeError;
		if (!isIntegerInRange(shift.lateGraceMinutes, 0, 1440)) {
			return { ok: false, message: '시프트 지각 허용 시간은 0분 이상으로 입력해주세요.' };
		}

		request.push({
			label,
			startTime: shift.startTime,
			endTime: shift.endTime,
			lateGraceMinutes: shift.lateGraceMinutes,
			displayOrder: index
		});
	}

	return { ok: true, request };
}

function validateBreaks(
	breaks: WorkPolicyBreak[]
): ValidationResult<WorkPolicyBreak[]> {
	const request: WorkPolicyBreak[] = [];
	for (const [index, br] of breaks.entries()) {
		const timeError = validateTimePair(
			br.startTime,
			br.endTime,
			`${index + 1}번째 휴게 시간을 모두 입력해주세요.`,
			`${index + 1}번째 휴게 시간 형식이 올바르지 않습니다.`,
			'휴게 종료 시간은 시작 시간보다 늦어야 합니다.'
		);
		if (timeError) return timeError;

		request.push({
			startTime: br.startTime,
			endTime: br.endTime,
			paid: br.paid,
			displayOrder: index
		});
	}

	return { ok: true, request };
}

function validateTimePair(
	start: string,
	end: string,
	missingMessage: string,
	invalidMessage: string,
	rangeMessage: string
): { ok: false; message: string } | null {
	if (!start || !end) {
		return { ok: false, message: missingMessage };
	}
	if (!isValidTimeString(start) || !isValidTimeString(end)) {
		return { ok: false, message: invalidMessage };
	}
	if (toMinutes(start) >= toMinutes(end)) {
		return { ok: false, message: rangeMessage };
	}
	return null;
}

function toMinutes(value: string): number {
	const [hours, minutes] = value.split(':').map(Number);
	return hours * 60 + minutes;
}

function isValidTimeString(value: string): boolean {
	return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function isIntegerInRange(value: number, min: number, max: number): boolean {
	return Number.isInteger(value) && value >= min && value <= max;
}

function isPositiveNumberAtMost(value: number, max: number): boolean {
	return Number.isFinite(value) && value > 0 && value <= max;
}

function isValidDateString(value: string): boolean {
	if (!DATE_PATTERN.test(value)) return false;
	const [year, month, day] = value.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	return (
		date.getFullYear() === year &&
		date.getMonth() === month - 1 &&
		date.getDate() === day
	);
}
